import React, { useLayoutEffect, useRef, useState } from 'react';
import BlockChip from './BlockChip';
import {
  DropPreview,
  Ghost,
  HourGutter,
  NowLine,
  TimedBlock,
  gridBackgroundFor,
  hourRange,
  timedTitle,
  useCreateDrag,
  useMoveDrag,
  useNowMin,
} from './timeGrid';
import { BLOCK_TYPES, doctorName, initials, isWeekend, layoutDay, todayISO } from './scheduleUtils';

// Narrow enough that a practice's doctors fit across a laptop screen without
// scrolling sideways; names truncate, the full detail is in each block's tooltip.
const COL_PX = 140;
// The hour height shrinks to fit the whole day on screen, but never below a
// size where a one-hour session is still readable.
const MIN_HOUR_PX = 30;
const MAX_HOUR_PX = 48;

// Live clientHeight of an element (follows window resizes and the header growing).
const useHeight = (ref) => {
  const [h, setH] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setH(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return h;
};

/**
 * Day view — one day, one column per doctor (the resource calendar salons,
 * clinics and OR boards use). Drag a session across columns to hand it to
 * another doctor; click a doctor's name to focus on just them.
 *
 * Built to fit the screen: the hours scale to the space available, and the
 * All day row only appears when someone is on call or away that day.
 */
const DayView = ({ day, doctors, blocks, hospitalsById, colours, conflicts, canEdit, me, onAdd, onEdit, onMove, onFocusDoctor }) => {
  const today = todayISO();
  const weekend = isWeekend(day);
  const dayBlocks = blocks.filter((b) => b.bdate === day);
  const timed = dayBlocks.filter((b) => BLOCK_TYPES[b.btype].timed);
  const allDay = dayBlocks.filter((b) => !BLOCK_TYPES[b.btype].timed);

  const scrollRef = useRef(null);
  const headRef = useRef(null);
  const boxHeight = useHeight(scrollRef);
  const headHeight = useHeight(headRef);
  const base = hourRange(timed);
  // 8px spare: the last hour label sits just below the grid, and must not force a scrollbar.
  const fit = boxHeight && headHeight ? Math.floor((boxHeight - headHeight - 8) / (base.endHour - base.startHour)) : MAX_HOUR_PX;
  const range = hourRange(timed, Math.max(MIN_HOUR_PX, Math.min(MAX_HOUR_PX, fit)));

  const nowMin = useNowMin();
  const create = useCreateDrag(range, (buid, bstart, bend) => onAdd({ buid, bdate: day, bstart, bend }));
  const move = useMoveDrag(range, (block, buid, bstart, bend, copy) => onMove(block, { buid, bdate: day, bstart, bend }, copy));

  const cols = `52px repeat(${doctors.length}, minmax(${COL_PX}px, 1fr))`;
  const minWidth = 52 + doctors.length * COL_PX;

  const summary = (uid) => {
    const mine = dayBlocks.filter((b) => b.buid === uid);
    if (mine.some((b) => b.btype === 'leave')) return { text: 'Away', cls: 'text-slate-400' };
    if (mine.length === 0) return weekend ? { text: 'Nothing scheduled', cls: 'text-slate-400' } : { text: 'Free', cls: 'font-semibold text-brand-mid' };
    const lists = mine.filter((b) => b.btype === 'theatre').length;
    const parts = [];
    if (lists) parts.push(`${lists} list${lists === 1 ? '' : 's'}`);
    if (mine.some((b) => b.btype === 'rooms')) parts.push('rooms');
    if (mine.some((b) => b.btype === 'on_call')) parts.push('on call');
    return { text: parts.join(' · '), cls: 'text-slate-400' };
  };

  return (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <div style={{ minWidth }}>
        <div ref={headRef} className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="grid" style={{ gridTemplateColumns: cols }}>
            <div />
            {doctors.map((d) => {
              const s = summary(d.uid);
              return (
                <button
                  key={d.uid}
                  type="button"
                  onClick={() => onFocusDoctor(d.uid)}
                  title={`${doctorName(d)} — show only this doctor`}
                  className="flex min-w-0 items-center gap-1.5 border-l border-slate-100 bg-white px-2 py-1.5 text-left transition-colors hover:bg-teal-50"
                >
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[10px] font-bold text-white" style={{ background: colours[d.uid] }}>
                    {initials(d)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-semibold leading-4 text-slate-800">
                      {doctorName(d)}
                      {d.uid === me && <span className="font-normal text-slate-400"> · you</span>}
                    </span>
                    <span className={`block truncate text-[11px] leading-4 ${s.cls}`}>{s.text}</span>
                  </span>
                </button>
              );
            })}

            {/* On-call and leave — only when someone has them today. */}
            {allDay.length > 0 && (
              <>
                <div className="border-t border-slate-100 px-1 py-1.5 text-right text-[10px] font-semibold uppercase leading-3 text-slate-400">All day</div>
                {doctors.map((d) => (
                  <div key={d.uid} className="flex min-h-[30px] flex-col gap-1 border-l border-t border-slate-100 p-1">
                    {allDay
                      .filter((b) => b.buid === d.uid)
                      .map((b) => (
                        <BlockChip
                          key={b.bid}
                          block={b}
                          hospital={hospitalsById[b.bhid]}
                          colour={colours[b.buid]}
                          conflict={conflicts.get(b.bid)}
                          draggable={false}
                          onClick={() => onEdit(b)}
                        />
                      ))}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        <div className="grid" style={{ gridTemplateColumns: cols }}>
          <HourGutter range={range} />
          {doctors.map((d) => {
            const editable = canEdit(d.uid);
            return (
              <div
                key={d.uid}
                className={`relative border-l border-slate-100 ${editable ? 'cursor-crosshair' : ''}`}
                style={{
                  ...gridBackgroundFor(range.hourPx),
                  height: range.height,
                  backgroundColor: day === today ? 'rgba(48, 207, 192, 0.04)' : weekend ? '#f8fafc' : undefined,
                }}
                onMouseDown={(e) => { if (editable) create.start(e, d.uid); }}
                {...move.columnProps(d.uid, () => editable)}
              >
                {layoutDay(timed.filter((b) => b.buid === d.uid)).map(({ block: b, col, cols: n }) => (
                  <TimedBlock
                    key={b.bid}
                    block={b}
                    col={col}
                    cols={n}
                    range={range}
                    colour={colours[b.buid]}
                    title={timedTitle(b, hospitalsById)}
                    detail={[b.btheatre, b.bsurgeon].filter(Boolean).join(' · ')}
                    notes={b.bnotes}
                    conflict={conflicts.get(b.bid)}
                    dragging={move.draggingBid === b.bid}
                    onClick={() => onEdit(b)}
                    dragProps={move.blockProps(b, canEdit(b.buid))}
                  />
                ))}
                {create.ghost?.key === d.uid && <Ghost ghost={create.ghost} range={range} />}
                {move.preview?.key === d.uid && <DropPreview preview={move.preview} range={range} colour={colours[move.preview.block.buid]} />}
                {day === today && <NowLine nowMin={nowMin} range={range} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DayView;
